import React, { useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { Loader2 } from "lucide-react";

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

/* =========================================================
   Error boundary — catches canvas render failures that
   escape react-pdf's own onLoadError callback
   ========================================================= */

interface PdfErrorBoundaryProps {
    children: React.ReactNode;
    onError: () => void;
}

interface PdfErrorBoundaryState {
    hasError: boolean;
}

class PdfErrorBoundary extends React.Component<
    PdfErrorBoundaryProps,
    PdfErrorBoundaryState
> {
    state: PdfErrorBoundaryState = { hasError: false };

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch() {
        this.props.onError();
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="flex h-full items-center justify-center px-6 text-center">
                    <p className="text-[12px] text-gray-500">
                        Unable to load the agreement.
                    </p>
                </div>
            );
        }
        return this.props.children;
    }
}

/* =========================================================
   Single page, lazily rendered when scrolled near
   ========================================================= */

interface PdfPageProps {
    pageNumber: number;
    width: number;
}

const PdfPage = ({ pageNumber, width }: PdfPageProps) => {
    const ref = useRef<HTMLDivElement | null>(null);
    const [isVisible, setIsVisible] = useState(false);
    const estimatedHeight = width * 1.4;

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.disconnect();
                }
            },
            { rootMargin: "600px 0px" }
        );

        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    return (
        <div ref={ref} style={{ minHeight: estimatedHeight }} className="w-full">
            {isVisible ? (
                <Page
                    pageNumber={pageNumber}
                    width={width}
                    renderAnnotationLayer={false}
                    renderTextLayer={false}
                    className="overflow-hidden rounded-lg shadow-sm"
                    loading={
                        <div
                            style={{ width, height: estimatedHeight }}
                            className="animate-pulse rounded-lg bg-gray-200"
                        />
                    }
                />
            ) : (
                <div
                    style={{ width, height: estimatedHeight }}
                    className="rounded-lg bg-gray-100"
                />
            )}
        </div>
    );
};

/* =========================================================
   Document container — measures width once, windowed pages
   ========================================================= */

interface PdfDocumentProps {
    file: { data: Uint8Array };
    onError: () => void;
}

const PdfDocument = ({ file, onError }: PdfDocumentProps) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [width, setWidth] = useState<number | null>(null);
    const [numPages, setNumPages] = useState<number | null>(null);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const update = () => setWidth(el.clientWidth);
        update();

        const observer = new ResizeObserver(update);
        observer.observe(el);

        return () => observer.disconnect();
    }, []);

    return (
        <div
            ref={containerRef}
            className="h-full w-full overflow-y-auto overflow-x-hidden bg-gray-50"
        >
            {width && (
                <Document
                    file={file}
                    onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                    onLoadError={onError}
                    loading={
                        <div className="flex h-40 items-center justify-center">
                            <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
                        </div>
                    }
                    error={
                        <div className="flex h-40 items-center justify-center px-6 text-center">
                            <p className="text-[12px] text-gray-500">
                                Unable to load the agreement.
                            </p>
                        </div>
                    }
                    className="flex flex-col items-center gap-3 px-2 py-3"
                >
                    {Array.from({ length: numPages ?? 0 }, (_, i) => (
                        <PdfPage key={i} pageNumber={i + 1} width={width - 16} />
                    ))}
                </Document>
            )}
        </div>
    );
};

/* =========================================================
   Public component: decodes base64 in a worker, then renders
   ========================================================= */

interface AgreementPdfViewerProps {
    base64: string | null;
    onError: () => void;
}

const AgreementPdfViewer = ({ base64, onError }: AgreementPdfViewerProps) => {
    const [file, setFile] = useState<{ data: Uint8Array } | null>(null);
    const [isDecoding, setIsDecoding] = useState(false);

    useEffect(() => {
        if (!base64) {
            setFile(null);
            return;
        }

        let cancelled = false;
        setIsDecoding(true);
        setFile(null);

        const worker = new Worker("/pdf-decode.worker.js");

        worker.onmessage = (
            e: MessageEvent<{ ok: boolean; bytes?: Uint8Array }>
        ) => {
            if (!cancelled) {
                if (e.data.ok && e.data.bytes) {
                    setFile({ data: e.data.bytes });
                } else {
                    onError();
                }
                setIsDecoding(false);
            }
            worker.terminate();
        };

        worker.onerror = () => {
            if (!cancelled) {
                onError();
                setIsDecoding(false);
            }
            worker.terminate();
        };

        worker.postMessage(base64);

        return () => {
            cancelled = true;
            worker.terminate();
        };
    }, [base64]);

    if (isDecoding) {
        return (
            <div className="flex h-full items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
            </div>
        );
    }

    if (!file) {
        return (
            <div className="flex h-full items-center justify-center px-6 text-center">
                <p className="text-[12px] text-gray-500">
                    Unable to load the agreement.
                </p>
            </div>
        );
    }

    return (
        <PdfErrorBoundary onError={onError}>
            <PdfDocument file={file} onError={onError} />
        </PdfErrorBoundary>
    );
};

export default AgreementPdfViewer;